import { useState } from 'react'
import { Check, Search, X } from 'lucide-react'
import styles from './MultiSelect.module.css'

export type MultiSelectOption = {
  count?: number
  id: string
  label: string
}

type MultiSelectProps<TOption extends MultiSelectOption> = {
  className?: string | undefined
  emptyText: string
  filterOption?: ((option: TOption, query: string) => boolean) | undefined
  onChange: (ids: string[]) => void
  options: TOption[]
  placeholder: string
  searchInputId?: string | undefined
  selectedIds: string[]
}

const cx = (...classNames: Array<string | false | null | undefined>): string =>
  classNames.filter(Boolean).join(' ')

const defaultFilterOption = <TOption extends MultiSelectOption>(option: TOption, query: string): boolean =>
  !query || option.label.toLowerCase().includes(query)

export const MultiSelect = <TOption extends MultiSelectOption>({
  className,
  emptyText,
  filterOption = defaultFilterOption,
  onChange,
  options,
  placeholder,
  searchInputId,
  selectedIds
}: MultiSelectProps<TOption>) => {
  const [search, setSearch] = useState('')
  const query = search.trim().toLowerCase()
  const filteredOptions = options.filter((option) => filterOption(option, query))
  const selectedOptions = options.filter((option) => selectedIds.includes(option.id))

  const toggleOption = (id: string): void => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((selectedId) => selectedId !== id)
        : [...selectedIds, id]
    )
  }

  return (
    <div className={cx(styles.root, 'multi-select-area', className)}>
      {selectedOptions.length > 0 && (
        <div className={cx(styles.selectedTags, 'multi-select-selected-tags')}>
          {selectedOptions.map((option) => (
            <span className={cx(styles.selectedTag, 'selected-tag')} key={option.id}>
              {option.label}
              <button
                aria-label={`Remove ${option.label}`}
                className={cx(styles.selectedTagRemove, 'selected-tag-remove')}
                onClick={(event) => {
                  event.stopPropagation()
                  toggleOption(option.id)
                }}
                type="button"
              >
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className={cx(styles.search, 'multi-select-search')}>
        <Search size={14} />
        <input
          aria-label={placeholder}
          id={searchInputId}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={placeholder}
          type="text"
          value={search}
        />
      </div>
      <div className={cx(styles.options, 'multi-select-options')}>
        {filteredOptions.length === 0 && (
          <div className={cx(styles.empty, 'multi-select-empty')}>{emptyText}</div>
        )}
        {filteredOptions.map((option) => {
          const selected = selectedIds.includes(option.id)

          return (
            <div
              aria-selected={selected}
              className={cx(styles.option, 'multi-select-option', selected && styles.optionSelected, selected && 'selected')}
              key={option.id}
              onClick={() => toggleOption(option.id)}
              role="option"
            >
              <span className={cx(styles.checkIcon, 'check-icon')}>
                <Check size={10} />
              </span>
              <span>{option.label}</span>
              {typeof option.count === 'number' && <span className={cx(styles.optionCount, 'option-count')}>{option.count}</span>}
            </div>
          )
        })}
      </div>
    </div>
  )
}
